// UserManagement.js
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Toast, ToastContainer, Spinner, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import CustomCard from '../components/CustomCard';
import ProjectContent from '../components/settingsContents/ProjectContent';
import { fetchCheckpoints } from '../features/checkpointsSlice';
import axiosInstance from '../axiosInstance';
import { OrgPreviewChart } from '../components/OrgPreview';
import { parseOrgExcel } from '../utils/parseOrgExcel';
import RichTextEditor from '../components/lexical/RichTextEditor';

const UserManagement = () => {
  const dispatch = useDispatch();

  // State for file upload section
  const [uploadedOrgData, setUploadedOrgData] = useState(null);
  const [previewOrgData, setPreviewOrgData] = useState(null);
  const [isParsing, setIsParsing] = useState(false); // Parsing Excel file
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  // State for the reminder section
  const [selectedCheckpoint, setSelectedCheckpoint] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [, setReminderSuccess] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [showReminderSuccessToast, setShowReminderSuccessToast] = useState(false);
  const [showReminderErrorToast, setShowReminderErrorToast] = useState(false);

  const [selectedRecipient, setSelectedRecipient] = useState('');

  // State for sending user credentials
  const [isSendingCredentials, setIsSendingCredentials] = useState(false);
  const [, setCredentialsSuccess] = useState(false);
  const [credentialsError, setCredentialsError] = useState('');
  const [showCredentialsSuccessToast, setShowCredentialsSuccessToast] = useState(false);
  const [showCredentialsErrorToast, setShowCredentialsErrorToast] = useState(false);
  const [showCredentialsConfirmModal, setShowCredentialsConfirmModal] = useState(false);
  const [credentialsStats, setCredentialsStats] = useState({ attempted: 0, sent: 0 });

  // Ref and state for clearing the rich text editor
  const editorRef = useRef(null);
  const orgFileInputRef = useRef(null);
  const [editorClearTrigger, setEditorClearTrigger] = useState(0);

  const visibleOrgData = previewOrgData || uploadedOrgData;
  const visibleFileName = selectedFile?.name || uploadedFileName;
  const hasPendingPreview = Boolean(previewOrgData && selectedFile);

  // Get checkpoints from the Redux store (populated via checkpointsSlice)
  const checkpoints = useSelector((state) => state.checkpoints.checkpoints);

  useEffect(() => {
    document.title = 'TRAC - User Management';
  }, []);

  // On mount, fetch checkpoints
  useEffect(() => {
    dispatch(fetchCheckpoints());
  }, [dispatch]);

  // File upload handlers
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setErrorMessage('');
      setUploadSuccess(false);
      setPreviewOrgData(null);

      // Parse the Excel file for preview
      try {
        setIsParsing(true);
        const parsed = await parseOrgExcel(file);
        setPreviewOrgData(parsed);
      } catch (error) {
        console.error('Parse error:', error);
        setErrorMessage(error.message || 'Fehler beim Parsen der Datei.');
        setShowErrorToast(true);
        setSelectedFile(null);
        if (orgFileInputRef.current) {
          orgFileInputRef.current.value = '';
        }
      } finally {
        setIsParsing(false);
      }
    }
  };

  // Cancel the preview and reset
  const handleCancelPreview = () => {
    setPreviewOrgData(null);
    setSelectedFile(null);
    if (orgFileInputRef.current) {
      orgFileInputRef.current.value = '';
    }
  };

  // Confirm and upload to backend
  const handleConfirmUpload = async () => {
    if (!selectedFile || !previewOrgData) {
      setErrorMessage('Bitte wähle eine Datei aus.');
      setShowErrorToast(true);
      return;
    }
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      setIsUploading(true);
      
      // Using axiosInstance to maintain consistency with other API calls
      await axiosInstance.post('/api/upload_users/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploadedOrgData(previewOrgData);
      setUploadedFileName(selectedFile.name);
      setUploadSuccess(true);
      setShowSuccessToast(true);
      setPreviewOrgData(null);
      
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage(
        error.response?.data?.detail || 
        error.message || 
        'Upload fehlgeschlagen. Bitte versuche es erneut.'
      );
      setShowErrorToast(true);
    } finally {
      setIsUploading(false);
    }
  };

  // Handler for sending user credentials
  const handleSendCredentials = async () => {
    setShowCredentialsConfirmModal(false);
    try {
      setIsSendingCredentials(true);
      setCredentialsError('');
      
      const response = await axiosInstance.post('/api/send_reset_emails/');
      
      setCredentialsStats({
        attempted: response.data.attempted || 0,
        sent: response.data.sent || 0
      });
      setCredentialsSuccess(true);
      setShowCredentialsSuccessToast(true);
    } catch (error) {
      console.error('Credentials send error:', error);
      setCredentialsError(
        error.response?.data?.error || 
        error.response?.data?.detail || 
        error.message || 
        'Fehler beim Senden der Zugangsdaten.'
      );
      setShowCredentialsErrorToast(true);
    } finally {
      setIsSendingCredentials(false);
    }
  };

  // Handler for sending a reminder email with JSON payload
  const handleSendReminder = async () => {
    // Validate that a recipient is selected
    if (!selectedRecipient) {
      setReminderError('Wähle bitte einen Empfänger aus.');
      setShowReminderErrorToast(true);
      return;
    }

    // Validate that a checkpoint and nonempty message have been selected
    if (!selectedCheckpoint) {
      setReminderError('Wähle bitte einen Checkpoint aus.');
      setShowReminderErrorToast(true);
      return;
    }
    if (!reminderMessage.trim()) {
      setReminderError('Gebe bitte eine Nachricht ein.');
      setShowReminderErrorToast(true);
      return;
    }
    try {
      setIsSendingReminder(true);
      // Send JSON payload with recipient information
      await axiosInstance.post(
        `/api/checkpoints/send_reminder/${selectedCheckpoint}/`,
        {
          message: reminderMessage,
          recipient: selectedRecipient
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setReminderSuccess(true);
      setShowReminderSuccessToast(true);
      setReminderMessage('');
      // Clear the rich text editor
      setEditorClearTrigger(prev => prev + 1);
    } catch (error) {
      setReminderError(
        error.response?.data?.detail || error.message || 'Fehler beim Senden der Erinnerung.'
      );
      setShowReminderErrorToast(true);
    } finally {
      setIsSendingReminder(false);
    }
  };

  return (
    <>
      <Container className="mt-3 flex-grow-1">
        <h2 className="mb-3">Organisationsstruktur</h2>
        <p>
          Um Deinen Führungskräften die passende Ansicht zur Verfügung zu stellen, müssen wir auch die Zusammenarbeit und
          Verbindungen Deiner Teams und Bereiche verstehen.
          <br />
          Folge hierfür bitte folgenden Schritten:
        </p>

        <Row className="mb-3">
          <Col sm={4} className="d-flex mb-3">
            <CustomCard title="Vorlage herunterladen" text="Lade unsere Vorlage herunter!">
              <Button variant="primary" href="/template.xlsx" download>
                Vorlage herunterladen
              </Button>
            </CustomCard>
          </Col>
          <Col sm={4} className="d-flex mb-3">
            <CustomCard
              title="Definition der Strukturen"
              text="Definiere Deine Teams/Bereiche (Blatt 1) und ordne Deinen Führungskräften die Teams/Bereiche zu (Blatt 2)."
            />
          </Col>
          <Col sm={4} className="d-flex mb-3">
            <CustomCard
              title="Upload"
              text="Lade die vervollständigte Datei nach gründlicher Kontrolle wieder bei uns hoch."
            />
          </Col>
        </Row>

        <hr />

        {/* File Upload Section */}
        <h3 className="mt-4"><b>Upload Deiner Organisationsstruktur</b></h3>
        <p>
          Bitte lade Deine ausgefüllte Excel-Datei hoch, um Deine Unternehmensstruktur zu visualisieren.
        </p>

        <Form>
          <Form.Group controlId="formFile" className={hasPendingPreview ? "mb-4" : (visibleFileName ? "" : "mb-4")}>
            <Form.Label>Datei hochladen</Form.Label>
            <Form.Control
              ref={orgFileInputRef}
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload} 
              className="org-upload-control"
            />
            {visibleFileName && (
              <Form.Text className="text-muted d-block mt-2">
                Aktuelle Datei: {visibleFileName}
              </Form.Text>
            )}
            {/*<Form.Text className="text-muted">
              Der Upload ist aktuell deaktiviert. Bitte versuche es später erneut.
            </Form.Text>*/}
          </Form.Group>

          {isParsing && (
            <div className="d-flex align-items-center mt-2 mb-3">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Datei wird analysiert...</span>
            </div>
          )}
        </Form>

        {/* Organization Structure Preview */}
        {visibleOrgData && (
          <div className="mt-4 mb-4">
            <OrgPreviewChart
              orgData={visibleOrgData}
              onConfirm={handleConfirmUpload}
              onCancel={handleCancelPreview}
              isConfirming={isUploading}
              showActions={hasPendingPreview}
              isSaved={uploadSuccess && !hasPendingPreview}
            />
          </div>
        )}

        <hr />

        {/* User Credentials Section */}
        <h3 className="mt-4"><b>Zugangsdaten an User verschicken</b></h3>
        <p>Über diese Funktion bekommt jeder angelegte User Zugangsdaten an die hinterlegte E-Mail geschickt.</p>
        <Form className="mb-4">
          <Button 
            variant="primary"
            onClick={() => setShowCredentialsConfirmModal(true)}
            disabled={isSendingCredentials}
          >
            {isSendingCredentials ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Wird gesendet...
              </>
            ) : (
              'Zugangsdaten senden'
            )}
          </Button>
        </Form>

        <hr />

        <ProjectContent />

        <hr />

        {/* Reminder Section */}
        <h3 className="mt-4"><b>Erinnerung</b></h3>
        <p>Sende eine Erinnerungsemail an die Verantwortlichen eines Checkpoints.</p>
        <Form className="mb-4">
          <Form.Group controlId="recipientSelect" className="mb-3">
            <Form.Label>Empfänger auswählen</Form.Label>
            <Form.Select
              value={selectedRecipient}
              onChange={(e) => setSelectedRecipient(e.target.value)}
            >
              <option value="">Bitte wählen...</option>
              <option value="führungskräfte">Führungskräfte</option>
              <option value="manager">Manager</option>
              <option value="alle">Alle</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="checkpointSelect" className="mb-3">
            <Form.Label>Checkpoint auswählen</Form.Label>
            <Form.Select
              value={selectedCheckpoint}
              onChange={(e) => setSelectedCheckpoint(e.target.value)}
            >
              <option value="">Bitte wählen...</option>
              {checkpoints.map((cp) => (
                <option key={cp.id} value={cp.id}>
                  {cp.title}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="reminderMessage" className="mb-3">
            <Form.Label>Nachricht</Form.Label>
            <RichTextEditor
              ref={editorRef}
              placeholder="Gebe Deine Erinnerung ein..."
              onChange={(html) => setReminderMessage(html)}
              clearTrigger={editorClearTrigger}
              namespace="ReminderEditor"
            />
          </Form.Group>
          <Button onClick={handleSendReminder} disabled={isSendingReminder}>
            {isSendingReminder ? 'Senden...' : 'Senden'}
          </Button>
        </Form>
      </Container>

      {/* Toast Notifications */}
      <ToastContainer
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1060,
        }}
        className="p-3"
      >
        {/* File Upload Success Toast */}
        <Toast
          onClose={() => setShowSuccessToast(false)}
          show={showSuccessToast}
          bg="success"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Erfolg</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">Datei erfolgreich hochgeladen!</Toast.Body>
        </Toast>

        {/* File Upload Error Toast */}
        <Toast
          onClose={() => setShowErrorToast(false)}
          show={showErrorToast}
          bg="danger"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Fehler</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">{errorMessage}</Toast.Body>
        </Toast>

        {/* Reminder Success Toast */}
        <Toast
          onClose={() => setShowReminderSuccessToast(false)}
          show={showReminderSuccessToast}
          bg="success"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Erinnerung gesendet</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            Die Erinnerung wurde erfolgreich versendet!
          </Toast.Body>
        </Toast>

        {/* Reminder Error Toast */}
        <Toast
          onClose={() => setShowReminderErrorToast(false)}
          show={showReminderErrorToast}
          bg="danger"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Fehler</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">{reminderError}</Toast.Body>
        </Toast>

        {/* Credentials Success Toast */}
        <Toast
          onClose={() => setShowCredentialsSuccessToast(false)}
          show={showCredentialsSuccessToast}
          bg="success"
          delay={7000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Zugangsdaten versendet</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            E-Mails wurden an {credentialsStats.sent} von {credentialsStats.attempted} Usern in die Warteschlange gestellt.
          </Toast.Body>
        </Toast>

        {/* Credentials Error Toast */}
        <Toast
          onClose={() => setShowCredentialsErrorToast(false)}
          show={showCredentialsErrorToast}
          bg="danger"
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Fehler</strong>
            <small>Jetzt</small>
          </Toast.Header>
          <Toast.Body className="text-white">{credentialsError}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Credentials Confirmation Modal */}
      <Modal 
        show={showCredentialsConfirmModal} 
        onHide={() => setShowCredentialsConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Zugangsdaten versenden</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Bist Du sicher, dass Du die Zugangsdaten an alle User versenden möchtest?</strong>
          </p>
          <p className="text-muted mb-0">
            Alle User Deines Unternehmens erhalten neue Passwörter per E-Mail. 
            Die vorherigen Passwörter werden dabei überschrieben.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCredentialsConfirmModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSendCredentials}>
            Ja, Zugangsdaten senden
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserManagement;
